import { css } from '@emotion/react';
import {
  type FC, type FormEvent, useCallback, useState,
} from 'react';
import { FormFieldType } from '~/_shared/utils/form/formValidation.helpers';
import { useFormFieldValidation } from '~/_shared/utils/form/useFormFieldValidation';
import { useTranslation } from '~/_shared/utils/hooks';
import { normalizeWhitespace } from '~/_shared/utils/text/text.helpers';
import type { UserRegisterErrors } from '~/store/userData/repository/userData.repository';
import { RegisterFormView } from './registerFormView.component';
import { type UserRegisterData } from './useUserRegister';

const defaultCursorStyle = css({
  cursor: 'default',
});

const centerTextStyle = css(defaultCursorStyle, {
  textAlign: 'center',
});

const headingPrimaryStyle = css(centerTextStyle, {
  fontSize: 22,
  margin: 0,
});

const headingSecondaryStyle = css(centerTextStyle, {
  fontSize: 14,
  margin: '8px 0 30px',
});

type RegisterFormProps = Readonly<{
  errorMessage?: string;
  errors?: UserRegisterErrors | null;
  isLoading: boolean;
  requireTOS: boolean;

  closeErrorMessage: () => void;
  onFormStart?: () => void;
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
  onSubmit: (data: UserRegisterData) => void;
}>;

export const RegisterFormComponent: FC<RegisterFormProps> = (props) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [areDetailsVisible, setAreDetailsVisible] = useState(false);
  const [t] = useTranslation();

  const { value: firstName, onBlur: onFirstNameBlur, onChange: onFirstNameChange, errors: firstNameErrors, validateValue: validateFirstNameValue } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('First name must not be empty'),
  });
  const { value: lastName, onBlur: onLastNameBlur, onChange: onLastNameChange, errors: lastNameErrors, validateValue: validateLastNameValue } = useFormFieldValidation({
    type: FormFieldType.Required,
    requiredFieldErrorMessage: t('Last name must not be empty'),
  });
  const { value: email, onBlur: onEmailBlur, onChange: onEmailChange, errors: emailErrors, validateValue: validateEmailValue } = useFormFieldValidation({
    type: FormFieldType.Email,
    additionalErrors: props.errors?.email,
  });
  const { value: password, onBlur: onPasswordBlur, onChange: onPasswordChange, errors: passwordErrors, validateValue: validatePasswordValue } = useFormFieldValidation({
    type: FormFieldType.Password,
    additionalErrors: props.errors?.password,
  });

  const { requireTOS } = props;

  const checkTerms = useCallback(() => {
    if (requireTOS && !termsAccepted) {
      setShowTermsError(true);
      return false;
    }
    return true;
  }, [requireTOS, termsAccepted]);

  const { onFormStart, onGoogleLogin: onGoogleLoginProp, onMicrosoftLogin: onMicrosoftLoginProp } = props;

  const onEmailFocus = useCallback(() => {
    setAreDetailsVisible(true);
    onFormStart?.();
  }, [onFormStart]);

  const onGoogleLogin = useCallback(() => {
    if (checkTerms()) {
      onGoogleLoginProp();
    }
  }, [checkTerms, onGoogleLoginProp]);

  const onMicrosoftLogin = useCallback(() => {
    if (checkTerms()) {
      onMicrosoftLoginProp();
    }
  }, [checkTerms, onMicrosoftLoginProp]);

  const onFormSubmit = (e: FormEvent) => {
    e.preventDefault();

    setAreDetailsVisible(true);

    const errors = [
      validateFirstNameValue(firstName),
      validateLastNameValue(lastName),
      validateEmailValue(email),
      validatePasswordValue(password),
    ];

    if (!checkTerms()) {
      return;
    }

    if (errors.every(e => !e.length)) {
      props.onSubmit({
        email,
        firstName: normalizeWhitespace(firstName),
        lastName: normalizeWhitespace(lastName),
        password,
      });
    }
  };

  return (
    <form
      autoComplete="off"
      onSubmit={onFormSubmit}
    >
      <p css={headingPrimaryStyle}>{t('Start your free, no risk, 10 day trial.')}</p>
      <p css={headingSecondaryStyle}>{t('No credit card required. No surprises. Just results.')}</p>

      <RegisterFormView
        areDetailsVisible={areDetailsVisible}
        email={{ value: email, errors: emailErrors, onBlur: onEmailBlur, onChange: onEmailChange }}
        firstName={{ value: firstName, errors: firstNameErrors, onBlur: onFirstNameBlur, onChange: onFirstNameChange }}
        lastName={{ value: lastName, errors: lastNameErrors, onBlur: onLastNameBlur, onChange: onLastNameChange }}
        password={{ value: password, errors: passwordErrors, onBlur: onPasswordBlur, onChange: onPasswordChange }}
        apiError={{ message: props.errorMessage, errors: props.errors, onClose: props.closeErrorMessage }}
        isLoading={props.isLoading}
        requireTOS={requireTOS}
        sso={{ onGoogleLogin, onMicrosoftLogin }}
        terms={{
          accepted: termsAccepted,
          showError: showTermsError,
          onChange: (checked) => {
            setTermsAccepted(checked);
            setShowTermsError(!checked);
          },
        }}
        onEmailFocus={onEmailFocus}
      />
    </form>
  );
};
